// frontend_react/src/components/AdminPanel/CompanyListPanel.jsx

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  getCompanies,
  updateCompany,
  deleteCompany as apiDeleteCompany,
} from "../../services/adminApi";
import logger from "../../utils/logger";
import { useAdminScope } from "../../context/AdminScopeContext";
import { useCompany } from "../../context/CompanyContext";
import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../pages/admin/ui_engine";
import { useAdminConfirmation } from "../../pages/admin/panel_engine/consumer";
import { AdminButton } from "../../pages/admin/action_engine";

import {
  AdminActions,
  AdminSectionBadge,
  AdminSectionHeader,
} from "../../pages/admin/section_engine";

const COMPANY_TYPE_LABELS = {
  operator: "Exploitant",
  supplier: "Fournisseur",
  customer: "Client",
  partner: "Partenaire",
  holding: "Holding",
};

const buildDraft = (company = {}) => ({
  company_type: company.company_type || "customer",

  name: company.name || "",
  legal_name: company.legal_name || "",
  commercial_name: company.commercial_name || "",

  siren: company.siren || "",
  siret: company.siret || "",
  tva_number: company.tva_number || "",
  naf_code: company.naf_code || "",

  legal_form: company.legal_form || "",
  share_capital: company.share_capital ?? "",
  rcs_city: company.rcs_city || "",
  rcs_number: company.rcs_number || "",

  email: company.email || "",
  phone: company.phone || "",
  website_url: company.website_url || "",

  address_line1: company.address_line1 || "",
  address_line2: company.address_line2 || "",
  postal_code: company.postal_code || "",
  city: company.city || "",
  country: company.country || "France",

  logo_url: company.logo_url || "",
  receipt_legal_notice: company.receipt_legal_notice || "",
  invoice_legal_notice: company.invoice_legal_notice || "",
});

export default function CompanyListPanel() {
  const [companies, setCompanies] = useState([]);
  const [editingUuid, setEditingUuid] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const { setSelectedCompany, selectedCompanyUuid } = useAdminScope();
  const { isSuperAdmin } = useCompany();
  const confirm = useAdminConfirmation();

  const handleSelectCompany = (company) => {
    if (!isSuperAdmin) return;

    setSelectedCompany(
      company.uuid,
      company.commercial_name || company.legal_name || company.name
    );

    setMessage(
      `Scope admin basculé sur : ${
        company.commercial_name || company.legal_name || company.name
      }`
    );
  };

  const loadCompanies = async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await getCompanies();
      setCompanies(data.companies || []);
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors du chargement des sociétés."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const startEdit = (company) => {
    setEditingUuid(company.uuid);
    setDraft(buildDraft(company));
    setMessage("");
    setError("");
  };

  const cancelEdit = () => {
    setEditingUuid(null);
    setDraft({});
  };

  const handleDraftChange = (event) => {
    const { name, value } = event.target;
    setDraft((prev) => ({ ...prev, [name]: value }));
  };

  const saveCompany = async (uuid) => {
    setMessage("");
    setError("");

    try {
      await updateCompany(uuid, {
        ...draft,
        share_capital: draft.share_capital ? Number(draft.share_capital) : null,
      });

      setMessage("Société mise à jour.");
      setEditingUuid(null);
      setDraft({});
      await loadCompanies();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la mise à jour."
      );
    }
  };

  const deleteCompany = async (uuid) => {
    const confirmed = await confirm({
      title: "Supprimer la société ?",
      message:
        "Confirmer la suppression de cette société ? L’historique doit être conservé côté backend par soft delete.",
      confirmLabel: "Supprimer",
      cancelLabel: "Conserver",
      intent: "danger",
    });

    if (!confirmed) return;

    setMessage("");
    setError("");

    try {
      await apiDeleteCompany(uuid);
      setMessage("Société supprimée.");
      await loadCompanies();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la suppression."
      );
    }
  };

  if (loading) {
    return (
      <AdminLoadingState message="Chargement des sociétés..." />
    );
  }

  return (
    <CompanyListWrapper>
      {message && <PanelMessage>{message}</PanelMessage>}
      {error && (
        <AdminErrorState
          message={error}
          compact
        />
      )}

      {companies.length === 0 ? (
        <AdminEmptyState message="Aucune société trouvée." />
      ) : (
        <CompanyCards>
          {companies.map((company) => {
            const isEditing = editingUuid === company.uuid;
            const displayName =
              company.commercial_name || company.legal_name || company.name;

            return (
              <CompanyCard key={company.uuid}>
                <CompanyTopLine>
                  <div>
                    <strong>{displayName}</strong>
                    <small>
                      {company.legal_name || company.name} ·{" "}
                      {company.siret || "SIRET non renseigné"}
                    </small>
                  </div>

                  <CompanyBadge>
                    {COMPANY_TYPE_LABELS[company.company_type] ||
                      company.company_type ||
                      "Société"}
                  </CompanyBadge>
                </CompanyTopLine>

                {isEditing ? (
                  <EditGrid>
                    <EditSection>
                      <AdminSectionHeader>
                        <AdminSectionBadge>Société</AdminSectionBadge>
                        <h4>Informations générales</h4>
                      </AdminSectionHeader>

                      <FieldsGrid>
                        <Field $full>
                          <label>Type de société *</label>
                          <select
                            name="company_type"
                            value={draft.company_type}
                            onChange={handleDraftChange}
                          >
                            <option value="operator">Exploitant Queen’s 360</option>
                            <option value="supplier">Fournisseur</option>
                            <option value="customer">Client professionnel</option>
                            <option value="partner">Partenaire</option>
                            <option value="holding">Holding / société du groupe</option>
                          </select>
                        </Field>

                        <Field $full>
                          <label>Nom *</label>
                          <input
                            name="name"
                            value={draft.name}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field $full>
                          <label>Raison sociale</label>
                          <input
                            name="legal_name"
                            value={draft.legal_name}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field $full>
                          <label>Nom commercial</label>
                          <input
                            name="commercial_name"
                            value={draft.commercial_name}
                            onChange={handleDraftChange}
                          />
                        </Field>
                      </FieldsGrid>
                    </EditSection>

                    <EditSection>
                      <AdminSectionHeader>
                        <AdminSectionBadge>Légal</AdminSectionBadge>
                        <h4>Immatriculation & mentions</h4>
                      </AdminSectionHeader>

                      <FieldsGrid>
                        <Field>
                          <label>SIREN</label>
                          <input
                            name="siren"
                            value={draft.siren}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>SIRET</label>
                          <input
                            name="siret"
                            value={draft.siret}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>N° TVA Intracommunautaire</label>
                          <input
                            name="tva_number"
                            value={draft.tva_number}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>Code NAF / APE</label>
                          <input
                            name="naf_code"
                            value={draft.naf_code}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>Forme juridique</label>
                          <input
                            name="legal_form"
                            value={draft.legal_form}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>Capital social</label>
                          <input
                            type="number"
                            step="0.01"
                            name="share_capital"
                            value={draft.share_capital}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>Ville RCS</label>
                          <input
                            name="rcs_city"
                            value={draft.rcs_city}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>N° RCS</label>
                          <input
                            name="rcs_number"
                            value={draft.rcs_number}
                            onChange={handleDraftChange}
                          />
                        </Field>
                      </FieldsGrid>
                    </EditSection>

                    <EditSection>
                      <AdminSectionHeader>
                        <AdminSectionBadge>Contact</AdminSectionBadge>
                        <h4>Email, téléphone & web</h4>
                      </AdminSectionHeader>

                      <FieldsGrid>
                        <Field>
                          <label>Email</label>
                          <input
                            type="email"
                            name="email"
                            value={draft.email}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>Téléphone</label>
                          <input
                            name="phone"
                            value={draft.phone}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field $full>
                          <label>Site web</label>
                          <input
                            name="website_url"
                            value={draft.website_url}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field $full>
                          <label>Logo URL</label>
                          <input
                            name="logo_url"
                            value={draft.logo_url}
                            onChange={handleDraftChange}
                          />
                        </Field>
                      </FieldsGrid>
                    </EditSection>

                    <EditSection>
                      <AdminSectionHeader>
                        <AdminSectionBadge>Adresse</AdminSectionBadge>
                        <h4>Coordonnées postales</h4>
                      </AdminSectionHeader>

                      <FieldsGrid>
                        <Field $full>
                          <label>Adresse ligne 1 *</label>
                          <input
                            name="address_line1"
                            value={draft.address_line1}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field $full>
                          <label>Adresse ligne 2</label>
                          <input
                            name="address_line2"
                            value={draft.address_line2}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>Code postal *</label>
                          <input
                            name="postal_code"
                            value={draft.postal_code}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>Ville *</label>
                          <input
                            name="city"
                            value={draft.city}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field>
                          <label>Pays *</label>
                          <input
                            name="country"
                            value={draft.country}
                            onChange={handleDraftChange}
                          />
                        </Field>
                      </FieldsGrid>
                    </EditSection>

                    <EditSection>
                      <AdminSectionHeader>
                        <AdminSectionBadge>PDF</AdminSectionBadge>
                        <h4>Mentions ticket & facture</h4>
                      </AdminSectionHeader>

                      <FieldsGrid>
                        <Field $full>
                          <label>Mention légale ticket</label>
                          <textarea
                            name="receipt_legal_notice"
                            value={draft.receipt_legal_notice}
                            onChange={handleDraftChange}
                          />
                        </Field>

                        <Field $full>
                          <label>Mention légale facture</label>
                          <textarea
                            name="invoice_legal_notice"
                            value={draft.invoice_legal_notice}
                            onChange={handleDraftChange}
                          />
                        </Field>
                      </FieldsGrid>
                    </EditSection>

                    <AdminActions>
                      <AdminButton
                        variant="success"
                        fullWidth
                        type="button"
                        onClick={() => saveCompany(company.uuid)}
                      >
                        Enregistrer
                      </AdminButton>
                      <AdminButton
                        variant="ghost"
                        fullWidth
                        type="button"
                        onClick={cancelEdit}
                      >
                        Annuler
                      </AdminButton>
                    </AdminActions>
                  </EditGrid>
                ) : (
                  <>
                    <CompanyMeta>
                      <span>
                        <b>Type :</b>{" "}
                        {COMPANY_TYPE_LABELS[company.company_type] ||
                          company.company_type ||
                          "—"}
                      </span>
                      <span>
                        <b>Email :</b> {company.email || "Non renseigné"}
                      </span>
                      <span>
                        <b>Téléphone :</b> {company.phone || "Non renseigné"}
                      </span>
                      <span>
                        <b>Adresse :</b> {company.address_line1}
                        {company.address_line2 ? `, ${company.address_line2}` : ""}
                      </span>
                      <span>
                        <b>Ville :</b> {company.postal_code} {company.city}
                      </span>
                      {company.website_url && (
                        <span>
                          <b>Web :</b> {company.website_url}
                        </span>
                      )}
                    </CompanyMeta>

                    <UuidLine>{company.uuid}</UuidLine>

                    <AdminActions>
                      {isSuperAdmin && (
                        <ScopeButton
                          type="button"
                          onClick={() => handleSelectCompany(company)}
                          $active={selectedCompanyUuid === company.uuid}
                        >
                          {selectedCompanyUuid === company.uuid
                            ? "Scope actif"
                            : "Basculer sur cette société"}
                        </ScopeButton>
                      )}

                      <AdminButton
                        variant="ghost"
                        fullWidth
                        type="button"
                        onClick={() => startEdit(company)}
                      >
                        Modifier
                      </AdminButton>

                      <AdminButton
                        variant="danger"
                        fullWidth
                        type="button"
                        onClick={() => deleteCompany(company.uuid)}
                      >
                        Supprimer
                      </AdminButton>
                    </AdminActions>
                  </>
                )}
              </CompanyCard>
            );
          })}
        </CompanyCards>
      )}
    </CompanyListWrapper>
  );
}

const CompanyListWrapper = styled.div`
  color: #f7f7f7;
`;

const CompanyCards = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const CompanyCard = styled.article`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const CompanyTopLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;

  strong {
    display: block;
    font-weight: 950;
  }

  small {
    display: block;
    margin-top: 0.25rem;
    opacity: 0.7;
    line-height: 1.35;
  }
`;

const CompanyBadge = styled.span`
  height: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
  white-space: nowrap;
`;

const CompanyMeta = styled.div`
  display: grid;
  gap: 0.35rem;
  margin-top: 0.9rem;
  font-size: 0.9rem;
  opacity: 0.86;
  line-height: 1.4;

  b {
    color: #fff9ea;
  }
`;

const UuidLine = styled.div`
  margin-top: 0.75rem;
  font-family: monospace;
  font-size: 0.72rem;
  opacity: 0.45;
  word-break: break-all;
`;

const EditGrid = styled.div`
  display: grid;
  gap: 0.95rem;
  margin-top: 1rem;
`;

const EditSection = styled.section`
  padding: 1rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.8;
  }

  input,
  select,
  textarea {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  input:focus,
  select:focus,
  textarea:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }

  textarea {
    resize: vertical;
    min-height: 110px;
  }
`;

const GhostButton = styled.button`
  flex: 1;
  border: none;
  border-radius: 10px;
  padding: 0.75rem;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
  cursor: pointer;
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: rgba(40, 140, 80, 0.9);
  font-weight: 850;
`;

const ScopeButton = styled(GhostButton)`
  background: ${({ $active }) => ($active ? "#d7ffd9" : "#fff9ea")};
  color: #0b1328;
`;
